/*
  File: src/components/tv/TvHero.jsx
  Purpose: TV page hero section with background art and CTAs.
  Props: { onWatch(): void }
*/
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPlus } from '@fortawesome/free-solid-svg-icons';

/**
 * TvHero
 * Top banner for TV page with background, text and CTAs.
 */
function TvHero({ onWatch, hasLiveSeries, liveSeries }) {
    const isSeriesEnabled = process.env.REACT_APP_ENABLE_SERIES === 'true';
    
    // Get the background image based on live series
    const getBackgroundImage = () => {
        if (hasLiveSeries && liveSeries?.thumbnail_url) {
            return liveSeries.thumbnail_url;
        }
        return `${process.env.PUBLIC_URL}/series.jpg`;
    };
    
    const backgroundImage = getBackgroundImage();
    return (
        <section className="relative overflow-hidden">
            <div className="absolute inset-0 fade-to-page" style={{ background: 'linear-gradient(to bottom, #0a1628 0%, rgba(10,22,40,0.98) 100%)' }}>
                <img 
                    src={backgroundImage} 
                    alt={hasLiveSeries ? (liveSeries.display_title || liveSeries.name || "Live Series") : "Series"} 
                    className="w-full h-full object-cover object-center brightness-110 contrast-105" 
                    onError={(e) => { e.currentTarget.src = `${process.env.PUBLIC_URL}/series.jpg`; }} 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/40 to-black/10" />
            </div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    {/* Left column: text and CTAs */}
                    <div className="space-y-6">
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-tight">
                            {hasLiveSeries ? (liveSeries.display_title || liveSeries.name || "Live Series") : "Series"}
                        </h1>
                        <p className="text-white/80 text-base sm:text-lg max-w-2xl">
                            {hasLiveSeries ? 
                                (liveSeries.description || `Watch ${liveSeries.display_title || liveSeries.name} live! Discover products in real-time while enjoying your favorite series.`) :
                                "You are in the Series section. Browse the series below to start watching and begin discovering products in real-time. Experience entertainment with integrated product detection technology."
                            }
                        </p>
                        <div className="text-xs text-white/60">{hasLiveSeries ? 'LIVE SERIES' : 'SERIES CONTENT'}</div>
                        <div className="flex flex-col sm:flex-row gap-4">
                            {!isSeriesEnabled ? (
                                <div className="group relative bg-gradient-to-r from-gray-600 to-gray-500 text-white/60 px-8 py-4 rounded-full font-semibold text-lg inline-flex items-center gap-2 cursor-not-allowed">
                                    <FontAwesomeIcon icon={faPlay} />
                                    <span>Feature Disabled</span>
                                </div>
                            ) : hasLiveSeries ? (
                                <button onClick={onWatch} className="group relative bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl inline-flex items-center gap-2 animate-pulse">
                                    <FontAwesomeIcon icon={faPlay} />
                                    <span>Keep Watching</span>
                                    <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs bg-white/20">
                                        🔴 LIVE
                                    </span>
                                </button>
                            ) : (
                                <div className="group relative bg-gradient-to-r from-gray-600 to-gray-500 text-white/60 px-8 py-4 rounded-full font-semibold text-lg inline-flex items-center gap-2 cursor-not-allowed">
                                    <FontAwesomeIcon icon={faPlay} />
                                    <span>No Live Series</span>
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-3 text-[10px]">
                            <span className="inline-flex items-center px-2 py-1 rounded bg-yellow-400 text-black font-bold">IMDb</span>
                            <span className="px-2 py-1 rounded border border-white/30">U/A</span>
                            <span className="px-2 py-1 rounded border border-white/30">4K</span>
                            <span className="px-2 py-1 rounded border border-white/30">2025</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default TvHero;

